import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authApi, tokenManager } from '../../services/api';
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('admin@klinnovac.com');
  const [password, setPassword] = useState('password123');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [forgotStep, setForgotStep] = useState(null);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await authApi.login(email, password);
      tokenManager.set(res.data.data.token, res.data.data.user);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Email atau password salah.');
    } finally {
      setLoading(false);
    }
  };

  // ==================== TECH BACKGROUND ====================
  const TechBackground = () => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Animated gradient layers */}
      <div className="absolute inset-0 opacity-60" style={{
        background: 'radial-gradient(ellipse 80% 60% at 50% 0%, rgba(0,207,255,0.12) 0%, transparent 60%), radial-gradient(ellipse 60% 80% at 0% 50%, rgba(0,179,166,0.08) 0%, transparent 50%), radial-gradient(ellipse 70% 50% at 100% 80%, rgba(63,223,255,0.1) 0%, transparent 50%)',
      }} />

      {/* Tech grid */}
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: 'linear-gradient(rgba(0,207,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(0,207,255,0.8) 1px, transparent 1px)',
        backgroundSize: '40px 40px',
        animation: 'gridScroll 20s linear infinite',
      }} />

      {/* Pulses */}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] rounded-full" style={{
        background: 'radial-gradient(circle, rgba(0,207,255,0.2) 0%, rgba(0,207,255,0.06) 50%, transparent 70%)',
        animation: 'techPulse 4s ease-in-out infinite',
        filter: 'blur(50px)',
      }} />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full" style={{
        background: 'radial-gradient(circle, rgba(0,179,166,0.18) 0%, rgba(0,179,166,0.05) 50%, transparent 70%)',
        animation: 'techPulse 6s ease-in-out infinite 2s',
        filter: 'blur(40px)',
      }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full" style={{
        background: 'radial-gradient(circle, rgba(63,223,255,0.1) 0%, transparent 60%)',
        animation: 'techPulse 8s ease-in-out infinite 4s',
        filter: 'blur(60px)',
      }} />

      {/* Scanning beam */}
      <div className="absolute left-0 right-0 h-0.5" style={{
        background: 'linear-gradient(90deg, transparent, rgba(0,207,255,0.6), rgba(63,223,255,0.9), rgba(0,207,255,0.6), transparent)',
        animation: 'scanBeam 3s linear infinite',
        boxShadow: '0 0 25px rgba(0,207,255,0.5), 0 0 80px rgba(0,207,255,0.2)',
      }} />

      {/* Expanding rings */}
      {[...Array(3)].map((_, i) => (
        <div key={`ring-${i}`} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-400/25" style={{
          width: `${180 + i * 160}px`,
          height: `${180 + i * 160}px`,
          animation: `ringExpand ${3 + i * 1.5}s ease-out infinite`,
          animationDelay: `${i * 1.2}s`,
        }} />
      ))}

      {/* Particles */}
      {[...Array(25)].map((_, i) => (
        <div key={`p-${i}`} className="absolute rounded-full" style={{
          left: `${5 + (i * 17) % 95}%`,
          top: `${3 + (i * 23) % 94}%`,
          width: `${1.5 + (i % 3)}px`,
          height: `${1.5 + (i % 3)}px`,
          background: ['#00CFFF', '#00B3A6', '#66E0FF', '#3FDFFF'][i % 4],
          boxShadow: `0 0 ${4 + (i % 6)}px currentColor`,
          animation: `particleFloat ${2.5 + (i % 3) * 1.5}s ease-in-out infinite`,
          animationDelay: `${i * 0.2}s`,
        }} />
      ))}

      {/* Diagonal light rays */}
      {[...Array(5)].map((_, i) => (
        <div key={`ray-${i}`} className="absolute" style={{
          left: `${-10 + i * 28}%`,
          top: '-20%',
          width: '1px',
          height: '140%',
          background: 'linear-gradient(to bottom, transparent, rgba(0,207,255,0.25), transparent)',
          transform: `rotate(${12 + i * 3}deg)`,
          animation: `rayMove ${5 + i * 1.5}s linear infinite`,
          animationDelay: `${i * 0.8}s`,
          boxShadow: '0 0 8px rgba(0,207,255,0.15)',
        }} />
      ))}

      {/* Hex node grid */}
      {[...Array(20)].map((_, i) => (
        <div key={`hex-${i}`} className="absolute" style={{
          left: `${8 + (i * 32) % 90}%`,
          top: `${4 + (i * 17) % 92}%`,
          width: '6px',
          height: '6px',
          background: i % 3 === 0 ? '#00CFFF' : '#0A3050',
          clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)',
          boxShadow: i % 3 === 0 ? '0 0 8px #00CFFF, 0 0 16px rgba(0,207,255,0.4)' : 'none',
          animation: `hexGlow ${2 + (i % 3)}s ease-in-out infinite`,
          animationDelay: `${i * 0.3}s`,
        }} />
      ))}
    </div>
  );

  return (
    <div className="fixed inset-0 w-screen h-screen overflow-hidden flex flex-col items-center justify-center px-2 py-2 sm:px-6 sm:py-4" style={{ background: 'linear-gradient(140deg, #010915 0%, #061530 25%, #0B2340 50%, #051728 75%, #020D1A 100%)' }}>
      <TechBackground />

      {/* Logo Section - Larger */}
      <div className="text-center mb-2 relative z-10 flex flex-col items-center flex-shrink-0">
        <div className="w-40 h-40 sm:w-56 sm:h-56 flex items-center justify-center mx-auto">
          <img src={require('../../logo.png')} alt="KLINNOVAC Logo" className="w-full h-full object-contain drop-shadow-2xl" />
        </div>
      </div>

      {/* Login Card - Transparent */}
      <div className="w-full max-w-sm bg-white/10 backdrop-blur-2xl border border-white/20 rounded-3xl p-3 sm:p-5 shadow-2xl shadow-cyan-500/10 relative z-10 flex-shrink-0">

        {error && (
          <div className="mb-2 p-2 bg-red-500/20 border border-red-400/50 rounded-lg flex items-start gap-2 text-xs text-red-200 backdrop-blur-sm">
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-3">
          {/* Email Field */}
          <div>
            <label className="block text-sm sm:text-base font-semibold text-white/90 mb-2">Email</label>
            <div className="relative">
              <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300/60" />
              <input 
                type="email" 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                placeholder="admin@klinnovac.com"
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-14 pr-4 py-3 sm:py-3.5 text-sm sm:text-base text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/50 focus:bg-white/15 outline-none transition backdrop-blur-sm" 
                autoComplete="email" 
                autoFocus 
              />
            </div>
          </div>

          {/* Password Field */}
          <div>
            <label className="block text-sm sm:text-base font-semibold text-white/90 mb-2">Password</label>
            <div className="relative">
              <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300/60 z-10" />
              <input 
                type={showPassword ? "text" : "password"}
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                placeholder="••••••••"
                className="w-full bg-white/10 border border-white/20 rounded-xl pl-14 pr-12 py-3 sm:py-3.5 text-sm sm:text-base text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/50 focus:bg-white/15 outline-none transition backdrop-blur-sm" 
                autoComplete="current-password" 
              />
              <button 
                type="button" 
                onClick={() => setShowPassword(!showPassword)} 
                className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-300/60 hover:text-cyan-300 transition z-10"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {/* Login Button */}
          <button 
            type="submit" 
            disabled={loading}
            className="w-full py-3 sm:py-3.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-bold text-sm sm:text-base hover:from-cyan-600 hover:to-teal-600 disabled:opacity-60 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-2xl hover:shadow-cyan-500/30 active:scale-95"
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : null}
            {loading ? 'Memproses...' : 'Masuk'}
          </button>

          {/* Forgot Password Link */}
          <div className="text-center pt-1">
            <button 
              type="button" 
              onClick={() => { setForgotStep(1); setError(''); }}
              className="text-xs sm:text-sm text-cyan-300/80 hover:text-cyan-300 transition underline"
            >
              Lupa Password?
            </button>
          </div>
        </form>

        {/* Footer Credit */}
        <p className="text-center text-xs text-white/50 mt-2 sm:mt-3">Klinnovac - Management System</p>
      </div>

      {/* ==================== FORGOT PASSWORD MODAL ==================== */}
      {forgotStep && (
        <div className="fixed inset-0 flex items-center justify-center z-[1000] px-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(4px)' }}>
          <div className="w-full max-w-sm bg-white/10 backdrop-blur-3xl border border-white/20 rounded-3xl p-4 sm:p-6 shadow-2xl shadow-cyan-500/10">
            
            {forgotStep === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <button 
                    onClick={() => setForgotStep(null)}
                    className="text-cyan-300/60 hover:text-cyan-300 transition"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <h2 className="text-lg sm:text-xl font-bold text-white flex-1">Lupa Password</h2>
                </div>
                <p className="text-xs sm:text-sm text-white/70">Masukkan email Anda untuk menerima kode reset password.</p>
                
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-2">Email</label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300/60" />
                    <input 
                      type="email" 
                      value={resetEmail} 
                      onChange={(e) => setResetEmail(e.target.value)} 
                      placeholder="admin@klinnovac.com"
                      className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-4 py-2.5 text-sm text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/50 outline-none transition"
                      autoFocus
                    />
                  </div>
                </div>

                <button 
                  onClick={() => {
                    if(!resetEmail) { setError('Email tidak boleh kosong'); return; }
                    setForgotStep(2);
                    setError('');
                  }}
                  className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-bold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl"
                >
                  Kirim Kode
                </button>
              </div>
            )}

            {forgotStep === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <button 
                    onClick={() => setForgotStep(1)}
                    className="text-cyan-300/60 hover:text-cyan-300 transition"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <h2 className="text-lg sm:text-xl font-bold text-white flex-1">Verifikasi Kode</h2>
                </div>
                <p className="text-xs sm:text-sm text-white/70">Kami telah mengirim kode verifikasi ke {resetEmail}</p>
                
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-2">Kode Verifikasi</label>
                  <input 
                    type="text" 
                    value={resetCode} 
                    onChange={(e) => setResetCode(e.target.value.toUpperCase())} 
                    placeholder="Masukkan 6 digit kode"
                    maxLength="6"
                    className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-sm text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/50 outline-none transition text-center font-mono text-lg tracking-widest"
                    autoFocus
                  />
                </div>

                <button 
                  onClick={() => {
                    if(!resetCode || resetCode.length < 6) { setError('Kode harus 6 digit'); return; }
                    setForgotStep(3);
                    setError('');
                  }}
                  className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-bold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl"
                >
                  Verifikasi
                </button>
              </div>
            )}

            {forgotStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <button 
                    onClick={() => setForgotStep(2)}
                    className="text-cyan-300/60 hover:text-cyan-300 transition"
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <h2 className="text-lg sm:text-xl font-bold text-white flex-1">Password Baru</h2>
                </div>
                <p className="text-xs sm:text-sm text-white/70">Masukkan password baru Anda</p>
                
                <div>
                  <label className="block text-sm font-semibold text-white/90 mb-2">Password Baru</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-cyan-300/60 z-10" />
                    <input 
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword} 
                      onChange={(e) => setNewPassword(e.target.value)} 
                      placeholder="Minimal 8 karakter"
                      className="w-full bg-white/10 border border-white/20 rounded-xl pl-12 pr-12 py-2.5 text-sm text-white placeholder-white/40 focus:ring-2 focus:ring-cyan-400 focus:border-cyan-400/50 outline-none transition"
                      autoFocus
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowNewPassword(!showNewPassword)} 
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-cyan-300/60 hover:text-cyan-300 transition z-10"
                    >
                      {showNewPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    if(!newPassword || newPassword.length < 8) { setError('Password minimal 8 karakter'); return; }
                    setForgotStep(null);
                    setResetEmail('');
                    setResetCode('');
                    setNewPassword('');
                    setShowNewPassword(false);
                    setPassword(newPassword);
                    setEmail(resetEmail || email);
                    setError('Password berhasil diperbarui. Silakan login.');
                  }}
                  className="w-full py-2.5 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-xl font-bold text-sm hover:from-cyan-600 hover:to-teal-600 transition-all shadow-lg hover:shadow-xl"
                >
                  Perbarui Password
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 p-2 bg-red-500/20 border border-red-400/50 rounded-lg flex items-start gap-2 text-xs text-red-200 backdrop-blur-sm">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}